import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import {
  disableTaskWorkflowConfig,
  fetchInstallLogs,
  fetchTaskWorkflowConfigs,
  saveTaskWorkflowConfig,
} from '../../store/slices/taskWorkflowConfigSlice';
import {
  GpuConfigInstallLog,
  TaskWorkflowConfig,
  TaskWorkflowConfigPayload,
} from '../../types';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

// 必须与客户端 distributed_config 里的 gpu_type 完全一致（区分大小写）
const GPU_TYPE_OPTIONS = ['klein', 'ltx', 'all_video', 'qwen21', 'minimax-h3'];

const formatDateTime = (value?: number | string) => {
  if (!value && value !== 0) return '-';
  if (typeof value === 'number') {
    const normalized = value < 1_000_000_000_000 ? value * 1000 : value;
    return new Date(normalized).toLocaleString();
  }
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? value : new Date(ts).toLocaleString();
};

const tierColor: Record<string, string> = {
  HOT: 'green',
  MODEL_ONLY: 'blue',
  RESTART: 'orange',
};

const statusColor: Record<string, string> = {
  SUCCESS: 'success',
  FAILED: 'error',
  IN_PROGRESS: 'processing',
  SKIPPED: 'default',
};

// 表单内 JSON 字段以字符串编辑，提交时解析为结构化对象
interface ConfigFormValues {
  id?: number;
  taskType: number;
  gpuType: string;
  workflowVersion: string;
  taskName?: string;
  workflowFile?: string;
  minClientVersion?: string;
  handler?: string;
  rolloutStrategy?: string;
  rolloutValue?: string;
  enabled: boolean;
  workflowJson?: string;
  inputMappings?: string;
  durationMappings?: string;
  seedMappings?: string;
  extraMappings?: string;
  requiredModels?: string;
  requiredCustomNodes?: string;
}

// 映射/依赖统一使用 snake_case（与 GPU 客户端 task_workflow_config.py 一致），不做键名风格校验
const parseJsonArray = (text: string | undefined, label: string): any[] | undefined => {
  const t = (text || '').trim();
  if (!t) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(t);
  } catch (e) {
    throw new Error(`${label} 不是合法的 JSON`);
  }
  if (!Array.isArray(parsed)) {
    throw new Error(`${label} 必须是 JSON 数组`);
  }
  return parsed as any[];
};

const parseJsonObject = (text: string | undefined, label: string): any | undefined => {
  const t = (text || '').trim();
  if (!t) return undefined;
  try {
    return JSON.parse(t);
  } catch (e) {
    throw new Error(`${label} 不是合法的 JSON`);
  }
};

const TaskWorkflowConfigManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { configs, installLogs, loading, saving, logsLoading } = useSelector(
    (state: RootState) => state.taskWorkflowConfig
  );
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [gpuTypeFilter, setGpuTypeFilter] = useState<string | undefined>(undefined);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<TaskWorkflowConfig | null>(null);
  const [form] = Form.useForm<ConfigFormValues>();

  const [logGpuId, setLogGpuId] = useState('');
  const [logTaskType, setLogTaskType] = useState<number | undefined>(undefined);

  const operator = currentUser?.username || 'admin';

  const refreshConfigs = useCallback(() => {
    dispatch(fetchTaskWorkflowConfigs(gpuTypeFilter));
  }, [dispatch, gpuTypeFilter]);

  useEffect(() => {
    refreshConfigs();
  }, [refreshConfigs]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      enabled: true,
      handler: 'standard',
      rolloutStrategy: 'ALL',
      gpuType: gpuTypeFilter || 'klein',
    });
    setModalVisible(true);
  };

  const openEdit = (record: TaskWorkflowConfig) => {
    setEditing(record);
    form.setFieldsValue({
      id: record.id,
      taskType: record.taskType,
      gpuType: record.gpuType,
      workflowVersion: record.workflowVersion,
      taskName: record.taskName,
      workflowFile: record.workflowFile,
      minClientVersion: record.minClientVersion,
      handler: record.handler || 'standard',
      rolloutStrategy: record.rolloutStrategy || 'ALL',
      rolloutValue: record.rolloutValue,
      enabled: record.enabled,
      workflowJson: record.workflowJson,
      inputMappings: record.inputMappings,
      durationMappings: record.durationMappings,
      seedMappings: record.seedMappings,
      extraMappings: record.extraMappings,
      requiredModels: record.requiredModels,
      requiredCustomNodes: record.requiredNodes,
    });
    setModalVisible(true);
  };

  const openCopy = (record: TaskWorkflowConfig) => {
    // 复制为新建：清空 id 与唯一键 taskType，其余字段沿用，taskName 追加 _copy
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      id: undefined,
      taskType: undefined as unknown as number,
      gpuType: record.gpuType,
      workflowVersion: record.workflowVersion,
      taskName: record.taskName ? `${record.taskName}_copy` : undefined,
      workflowFile: record.workflowFile,
      minClientVersion: record.minClientVersion,
      handler: record.handler || 'standard',
      rolloutStrategy: record.rolloutStrategy || 'ALL',
      rolloutValue: record.rolloutValue,
      enabled: record.enabled,
      workflowJson: record.workflowJson,
      inputMappings: record.inputMappings,
      durationMappings: record.durationMappings,
      seedMappings: record.seedMappings,
      extraMappings: record.extraMappings,
      requiredModels: record.requiredModels,
      requiredCustomNodes: record.requiredNodes,
    });
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditing(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    let values: ConfigFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    let payload: TaskWorkflowConfigPayload;
    try {
      payload = {
        id: values.id,
        taskType: values.taskType,
        gpuType: values.gpuType.trim(),
        workflowVersion: values.workflowVersion.trim(),
        taskName: values.taskName,
        workflowFile: values.workflowFile,
        minClientVersion: values.minClientVersion,
        handler: values.handler,
        rolloutStrategy: values.rolloutStrategy,
        rolloutValue: values.rolloutValue,
        enabled: values.enabled,
        workflowJson: parseJsonObject(values.workflowJson, 'workflowJson'),
        inputMappings: parseJsonArray(values.inputMappings, 'inputMappings'),
        durationMappings: parseJsonArray(values.durationMappings, 'durationMappings'),
        seedMappings: parseJsonArray(values.seedMappings, 'seedMappings'),
        extraMappings: parseJsonArray(values.extraMappings, 'extraMappings'),
        requiredModels: parseJsonArray(values.requiredModels, 'requiredModels'),
        requiredCustomNodes: parseJsonArray(
          values.requiredCustomNodes,
          'requiredCustomNodes'
        ),
        operator,
      };
    } catch (e: any) {
      message.error(e.message || 'JSON 字段解析失败');
      return;
    }

    try {
      await dispatch(saveTaskWorkflowConfig(payload)).unwrap();
      message.success(editing ? '配置已更新' : '配置已创建');
      closeModal();
      refreshConfigs();
    } catch (e: any) {
      message.error(typeof e === 'string' ? e : '保存配置失败');
    }
  };

  const handleDisable = async (record: TaskWorkflowConfig) => {
    try {
      await dispatch(disableTaskWorkflowConfig(record.id)).unwrap();
      message.success('配置已禁用');
      refreshConfigs();
    } catch (e: any) {
      message.error(typeof e === 'string' ? e : '禁用配置失败');
    }
  };

  const queryLogs = useCallback(() => {
    dispatch(
      fetchInstallLogs({
        gpuId: logGpuId.trim() || undefined,
        taskType: logTaskType,
      })
    );
  }, [dispatch, logGpuId, logTaskType]);

  const configColumns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', width: 70 },
      { title: '任务类型', dataIndex: 'taskType', width: 100 },
      {
        title: 'GPU 类型',
        dataIndex: 'gpuType',
        width: 110,
        render: (v: string) => <Tag color="geekblue">{v}</Tag>,
      },
      { title: '版本', dataIndex: 'workflowVersion', width: 100 },
      { title: '任务名', dataIndex: 'taskName', ellipsis: true },
      {
        title: 'handler',
        dataIndex: 'handler',
        width: 100,
        render: (v?: string) => v || 'standard',
      },
      {
        title: '灰度',
        key: 'rollout',
        width: 140,
        render: (_: unknown, r: TaskWorkflowConfig) =>
          r.rolloutStrategy && r.rolloutStrategy !== 'ALL'
            ? `${r.rolloutStrategy}:${r.rolloutValue || ''}`
            : 'ALL',
      },
      {
        title: '状态',
        dataIndex: 'enabled',
        width: 90,
        render: (v: boolean) => (
          <Tag color={v ? 'success' : 'default'}>{v ? '启用' : '禁用'}</Tag>
        ),
      },
      {
        title: '更新时间',
        dataIndex: 'updatedAt',
        width: 180,
        render: (v: number | string) => formatDateTime(v),
      },
      {
        title: '操作',
        key: 'actions',
        fixed: 'right' as const,
        width: 220,
        render: (_: unknown, record: TaskWorkflowConfig) => (
          <Space>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            >
              编辑
            </Button>
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => openCopy(record)}
            >
              复制
            </Button>
            <Popconfirm
              title="确认禁用该配置？"
              onConfirm={() => handleDisable(record)}
              okText="禁用"
              cancelText="取消"
              disabled={!record.enabled}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<StopOutlined />}
                disabled={!record.enabled}
              >
                禁用
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const logColumns = useMemo(
    () => [
      {
        title: '时间',
        dataIndex: 'createdAt',
        width: 180,
        render: (v: number | string) => formatDateTime(v),
      },
      { title: 'GPU ID', dataIndex: 'gpuId', width: 160, ellipsis: true },
      { title: 'Host ID', dataIndex: 'hostId', width: 140, ellipsis: true },
      { title: '任务类型', dataIndex: 'taskType', width: 90 },
      { title: '版本', dataIndex: 'workflowVersion', width: 90 },
      {
        title: 'Tier',
        dataIndex: 'tier',
        width: 110,
        render: (v: string) => <Tag color={tierColor[v] || 'default'}>{v}</Tag>,
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 110,
        render: (v: string) => <Tag color={statusColor[v] || 'default'}>{v}</Tag>,
      },
      {
        title: '耗时(ms)',
        dataIndex: 'durationMs',
        width: 100,
        render: (v?: number) => (v != null ? v : '-'),
      },
      { title: '错误', dataIndex: 'error', ellipsis: true, render: (v?: string) => v || '-' },
    ],
    []
  );

  const jsonField = (
    name: keyof ConfigFormValues,
    label: string,
    placeholder: string,
    rows = 4
  ) => (
    <Form.Item name={name} label={label}>
      <TextArea rows={rows} placeholder={placeholder} style={{ fontFamily: 'monospace' }} />
    </Form.Item>
  );

  const configTab = (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder="按 GPU 类型筛选"
          style={{ width: 200 }}
          value={gpuTypeFilter}
          onChange={(v) => setGpuTypeFilter(v)}
          options={GPU_TYPE_OPTIONS.map((t) => ({ label: t, value: t }))}
        />
        <Button icon={<ReloadOutlined />} onClick={refreshConfigs}>
          刷新
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新增配置
        </Button>
      </Space>
      <Table<TaskWorkflowConfig>
        rowKey="id"
        loading={loading}
        dataSource={configs}
        columns={configColumns}
        scroll={{ x: 1200 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </>
  );

  const logsTab = (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="GPU ID"
          allowClear
          style={{ width: 200 }}
          value={logGpuId}
          onChange={(e) => setLogGpuId(e.target.value)}
        />
        <InputNumber
          placeholder="任务类型"
          style={{ width: 140 }}
          value={logTaskType}
          onChange={(v) => setLogTaskType(v ?? undefined)}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={queryLogs}>
          查询
        </Button>
      </Space>
      <Table<GpuConfigInstallLog>
        rowKey="id"
        loading={logsLoading}
        dataSource={installLogs}
        columns={logColumns}
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </>
  );

  return (
    <Card title="任务 Workflow 配置管理">
      <Paragraph type="secondary">
        在线维护各 GPU 类型的任务 workflow 定义（参数映射、依赖与下载命令、灰度）。
        保存后由 GPU 客户端通过心跳 configUpdates 拉取并按 HOT / MODEL_ONLY / RESTART 生效。
      </Paragraph>
      <Tabs
        defaultActiveKey="configs"
        items={[
          { key: 'configs', label: '配置列表', children: configTab },
          { key: 'logs', label: '安装日志', children: logsTab },
        ]}
      />

      <Modal
        title={editing ? `编辑配置 #${editing.id}` : '新增任务 Workflow 配置'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={saving}
        width={860}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Space size="large" style={{ display: 'flex' }} wrap>
            <Form.Item
              name="taskType"
              label="任务类型(taskType)"
              rules={[{ required: true, message: '请输入任务类型' }]}
            >
              <InputNumber style={{ width: 180 }} disabled={!!editing} />
            </Form.Item>
            <Form.Item
              name="gpuType"
              label="GPU 类型(gpuType)"
              rules={[{ required: true, message: '请选择 GPU 类型' }]}
            >
              <Select
                style={{ width: 180 }}
                placeholder="请选择 GPU 类型"
                disabled={!!editing}
                options={GPU_TYPE_OPTIONS.map((t) => ({ label: t, value: t }))}
              />
            </Form.Item>
            <Form.Item
              name="workflowVersion"
              label="配置版本(workflowVersion)"
              rules={[{ required: true, message: '请输入版本号' }]}
            >
              <Input style={{ width: 160 }} placeholder="1.0.0" />
            </Form.Item>
            <Form.Item name="enabled" label="启用" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          <Space size="large" style={{ display: 'flex' }} wrap>
            <Form.Item name="taskName" label="任务名(taskName)">
              <Input style={{ width: 220 }} />
            </Form.Item>
            <Form.Item name="workflowFile" label="workflowFile">
              <Input style={{ width: 220 }} placeholder="xxx_api.json" />
            </Form.Item>
            <Form.Item name="minClientVersion" label="最低客户端版本">
              <Input style={{ width: 160 }} placeholder="1.0.3" />
            </Form.Item>
            <Form.Item name="handler" label="handler">
              <Select
                style={{ width: 140 }}
                options={[
                  { label: 'standard', value: 'standard' },
                  { label: 'native', value: 'native' },
                ]}
              />
            </Form.Item>
          </Space>

          <Space size="large" style={{ display: 'flex' }} wrap>
            <Form.Item name="rolloutStrategy" label="灰度策略">
              <Select
                style={{ width: 160 }}
                options={[
                  { label: 'ALL', value: 'ALL' },
                  { label: 'PERCENT', value: 'PERCENT' },
                  { label: 'GPU_LIST', value: 'GPU_LIST' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="rolloutValue"
              label="灰度值(百分比 / gpuId 列表)"
              tooltip="PERCENT 填 0-100；GPU_LIST 填逗号分隔的 gpuId"
            >
              <Input style={{ width: 320 }} placeholder="20 或 gpu-a,gpu-b" />
            </Form.Item>
          </Space>

          {jsonField(
            'workflowJson',
            'workflowJson（内联 ComfyUI workflow，JSON 对象，可空）',
            '{ "76": { "inputs": { "image": "x.webp" }, "class_type": "LoadImage" } }',
            5
          )}
          {jsonField(
            'inputMappings',
            'inputMappings（JSON 数组，snake_case）',
            '[{"payload_key":"img1","node_key":"76","input_name":"image","value_type":"image","required":true}]'
          )}
          {jsonField(
            'durationMappings',
            'durationMappings（JSON 数组，ltx 等使用，snake_case）',
            '[{"payload_key":"seconds","node_key":"261","input_name":"value","value_type":"int"}]',
            3
          )}
          {jsonField(
            'seedMappings',
            'seedMappings（JSON 数组，snake_case）',
            '[{"node_key":"153","input_name":"seed"}]',
            3
          )}
          {jsonField(
            'extraMappings',
            'extraMappings（JSON 数组，bool/开关，如 all_video 的 enable_music）',
            '[{"payload_key":"enable_music","node_key":"1","input_name":"boolean","value_type":"bool","default_value":false}]',
            3
          )}
          {jsonField(
            'requiredModels',
            'requiredModels（JSON 数组，snake_case，含 download_cmd/check_exists/sha256/requires_restart）',
            '[{"name":"m.safetensors","target_path":"{comfyRoot}/models/checkpoints/m.safetensors","check_exists":"{comfyRoot}/models/checkpoints/m.safetensors","sha256":"...","download_cmd":"gsutil -m cp gs://b/m.safetensors {targetPath}","requires_restart":false}]',
            5
          )}
          {jsonField(
            'requiredCustomNodes',
            'requiredCustomNodes（JSON 数组，自定义节点，requires_restart 通常为 true）',
            '[{"name":"ComfyUI-X","target_path":"{comfyRoot}/custom_nodes/ComfyUI-X","check_exists":"{comfyRoot}/custom_nodes/ComfyUI-X","download_cmd":"git clone https://github.com/org/ComfyUI-X {targetPath}","post_install_cmd":"pip install -r {targetPath}/requirements.txt","requires_restart":true}]',
            5
          )}
          <Text type="secondary">
            提示：映射/依赖字段统一使用 snake_case（与 GPU 客户端配置一致，可直接粘贴）；命令仅支持白名单前缀（gsutil/wget/curl/git/pip/hf 等）；占位符支持 {'{comfyRoot}'} / {'{targetPath}'} / {'{env}'}。
          </Text>
        </Form>
      </Modal>
    </Card>
  );
};

export default TaskWorkflowConfigManagement;
